import { Link, useNavigate, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Zap, Dumbbell, Timer, Heart, Apple, ChevronRight, Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const LOGO_URL = '/logo.webp';
const TEXT_LOGO_URL = '/img/landing-hero.webp';

const features = [
  { icon: Dumbbell, title: 'Expert Workouts', desc: 'Strength, HIIT, Cardio & Yoga programs designed by professionals' },
  { icon: Timer, title: 'Built-in Timer', desc: 'Track your intervals and rest periods with precision timing' },
  { icon: Heart, title: 'Progress Tracking', desc: 'Monitor your fitness journey with detailed statistics' },
  { icon: Apple, title: 'Nutrition Tips', desc: 'Fuel your body with expert nutrition guidance' }
];

const categories = [
  { name: 'Strength', image: 'https://images.pexels.com/photos/7690454/pexels-photo-7690454.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', count: '3 Workouts' },
  { name: 'HIIT', image: 'https://images.unsplash.com/photo-1601422407692-ec4eeec1d9b3?w=800', count: '2 Workouts' },
  { name: 'Cardio', image: 'https://images.unsplash.com/photo-1620123449238-abaeff62d48d?w=800', count: '2 Workouts' },
  { name: 'Yoga', image: 'https://images.pexels.com/photos/4325475/pexels-photo-4325475.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', count: '2 Workouts' }
];

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // If a logged-in user lands here (e.g. via accidental route fallthrough,
  // a stale link from a notification, or returning from a buddy invite),
  // bounce them straight to /dashboard so they never see the public
  // "Sign in / Get Started" page while authenticated.
  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-black" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Ascend & Surpass" className="h-12 w-12" />
            <img src={TEXT_LOGO_URL} alt="Ascend & Surpass" className="h-8 hidden sm:block" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:text-primary font-heading tracking-wider" data-testid="nav-login-btn">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-primary text-primary-foreground font-heading tracking-wider hover:bg-primary/90" data-testid="nav-register-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center">
        <div className="fixed inset-0 bg-black pointer-events-none">
          <img 
            src="/img/auth-hero.webp"
            alt="Ascend & Surpass Fitness Team"
            className="absolute right-0 top-1/2 -translate-y-1/2 h-[85%] w-auto object-contain"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        </div>
        
        <div className="relative z-10 container mx-auto px-6 pt-16 md:pt-32">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-2 mb-3 md:mb-6">
              <Zap className="h-5 w-5 md:h-6 md:w-6 text-primary" />
              <span className="text-primary font-heading text-sm md:text-xl tracking-widest uppercase">Ignite Your Power</span>
            </div>
            
            <h1 className="font-heading text-4xl md:text-8xl text-white uppercase tracking-wider leading-none mb-3 md:mb-6">
              Ascend<br/>
              <span className="text-primary">&</span> Surpass
            </h1>
            
            <p className="text-white/70 text-base md:text-2xl mb-6 md:mb-10 max-w-xl leading-relaxed">
              Activation, not just fitness. Build real power through daily discipline and ignite your transformation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Button 
                onClick={() => navigate('/register')}
                className="h-12 md:h-16 px-8 md:px-10 bg-primary text-primary-foreground font-heading text-base md:text-xl tracking-wider uppercase hover:bg-primary/90 transition-all hover:-translate-y-1"
                data-testid="hero-cta-btn"
              >
                <Zap className="mr-2 h-5 w-5 md:h-6 md:w-6" />
                Start Training
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/login')}
                className="h-12 md:h-16 px-8 md:px-10 border-white/30 text-white font-heading text-base md:text-xl tracking-wider uppercase hover:bg-white/10"
                data-testid="hero-signin-btn"
              >
                <Play className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                Sign In
              </Button>
            </div>
          </motion.div>
        </div>
        
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="h-8 w-8 text-primary rotate-90" />
        </div>
      </section>

      {/* Features */}
      <section className="py-24 relative z-10">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-5xl md:text-6xl text-white uppercase tracking-wider mb-4">Why Choose Us</h2>
            <div className="h-1 w-24 bg-primary mx-auto" />
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-black border border-border p-8 card-hover group"
              >
                <div className="h-14 w-14 bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 bg-black relative z-10">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-5xl md:text-6xl text-white uppercase tracking-wider mb-4">Workout Categories</h2>
            <div className="h-1 w-24 bg-primary mx-auto" />
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative h-80 overflow-hidden group cursor-pointer"
                onClick={() => navigate('/register')}
              >
                <img 
                  src={cat.image}
                  alt={cat.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-primary text-sm font-semibold uppercase tracking-wider">{cat.count}</span>
                  <h3 className="font-heading text-3xl text-white uppercase tracking-wider">{cat.name}</h3>
                </div>
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary transition-colors" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#09090B] relative z-10 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <img src={LOGO_URL} alt="" className="w-full h-full object-contain" />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-2xl">
            <h2 className="font-heading text-5xl md:text-7xl text-white uppercase tracking-wider mb-6">
              Ready to<br/><span className="text-primary">Transform?</span>
            </h2>
            <p className="text-white/70 text-xl mb-10">
              Take the first step towards your fitness goals. Join thousands who have already begun their transformation journey.
            </p>
            <Button 
              onClick={() => navigate('/register')}
              className="h-16 px-12 bg-primary text-primary-foreground font-heading text-xl tracking-wider uppercase hover:bg-primary/90 animate-pulse-glow"
              data-testid="cta-btn"
            >
              Start Your Journey
              <ChevronRight className="ml-2 h-6 w-6" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-border relative z-10">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
              <img src={TEXT_LOGO_URL} alt="Ascend & Surpass" className="h-6" />
            </div>
            <p className="text-muted-foreground text-sm">
              © 2024 Ascend & Surpass Fitness. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
